import { Star } from "lucide-react";
import { Fragment } from "react";

import { STAR_GOLD_EDGE, STAR_GOLD_FILL } from "#/components/ChallengeStarIcon";
import { ActivityFillCell } from "#/components/ui/activity-fill-cell";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "#/components/ui/card";
import { Infotip, InfotipContent, InfotipProvider, InfotipTrigger } from "#/components/ui/infotip";
import { Skeleton } from "#/components/ui/skeleton";
import { profileActivityFillLabel, resolveActivityFillFromCompletion } from "#/lib/calendarHeatmap";
import { cn } from "#/lib/utils";
import type {
  ProfileActivityData,
  ProfileActivityDay,
  ProfileActivityWeek,
} from "#/server/game/profileActivity";

import { ActivityFillLegend } from "./-activityFill.legend";

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

function monthLabelsForWeeks({
  weeks,
  endDateStr,
}: {
  weeks: Array<{ days: ProfileActivityDay[] }>;
  endDateStr: string;
}): string[] {
  const labels: string[] = [];
  let lastMonth = "";
  for (const week of weeks) {
    const firstDated = week.days.find((day) => day.date && day.date <= endDateStr);
    const month = firstDated?.date?.slice(0, 7) ?? "";
    if (month && month !== lastMonth) {
      labels.push(month);
      lastMonth = month;
    } else {
      labels.push("");
    }
  }
  return labels;
}

function formatMonthLabel(monthKey: string): string {
  if (!monthKey) return "";
  const year = Number(monthKey.slice(0, 4));
  const monthIndex = Number(monthKey.slice(5, 7)) - 1;
  return new Intl.DateTimeFormat("en-US", { month: "short", timeZone: "UTC" }).format(
    new Date(Date.UTC(year, monthIndex, 1)),
  );
}

function activityDayFill(day: ProfileActivityDay) {
  if (!day.date) return null;
  return resolveActivityFillFromCompletion({
    date: day.date,
    standardCompleted: day.standardCompleted,
    premiumCompleted: day.premiumCompleted,
  });
}

function activityDayTitle(day: ProfileActivityDay): string {
  if (!day.date) return "";
  const fill = activityDayFill(day);
  if (!fill) return "";
  return `${day.date}: ${profileActivityFillLabel(fill)}`;
}

function ActivityDayCell({ day }: { day: ProfileActivityDay }) {
  if (!day.date) {
    return <div className="aspect-square min-h-2.5 min-w-2.5 w-full" aria-hidden />;
  }

  const label = activityDayTitle(day);

  return (
    <Infotip>
      <InfotipTrigger
        aria-label={label}
        className="flex aspect-square min-h-2.5 min-w-2.5 w-full cursor-default"
      >
        <ActivityFillCell fill={activityDayFill(day) ?? "empty"} size="full" />
      </InfotipTrigger>
      <InfotipContent side="top">{label}</InfotipContent>
    </Infotip>
  );
}

function ChallengeWeekStarCell({ challengeCompleted }: { challengeCompleted: boolean | null }) {
  if (challengeCompleted === null) {
    return <div className="aspect-square min-h-2.5 min-w-2.5 w-full" aria-hidden />;
  }

  const label = challengeCompleted ? "Challenge cleared" : "Challenge not cleared";

  return (
    <Infotip>
      <InfotipTrigger
        aria-label={label}
        className="flex aspect-square min-h-2.5 min-w-2.5 w-full cursor-default items-center justify-center overflow-hidden"
      >
        <Star
          className={cn(
            "block max-h-full max-w-full min-h-0 min-w-0 size-full",
            !challengeCompleted && "fill-muted text-muted",
          )}
          width="100%"
          height="100%"
          style={challengeCompleted ? { fill: STAR_GOLD_FILL, color: STAR_GOLD_EDGE } : undefined}
          strokeWidth={1.5}
          aria-hidden
        />
      </InfotipTrigger>
      <InfotipContent side="top">{label}</InfotipContent>
    </Infotip>
  );
}

function activityGridColumns(weekCount: number): string {
  return `1.5rem repeat(${weekCount}, minmax(0.625rem, 1fr))`;
}

function activityGridMinWidth(weekCount: number): string {
  return `calc(1.5rem + ${weekCount} * (0.625rem + 3px))`;
}

function monthLabelAlignsEnd({
  weekIndex,
  weekCount,
}: {
  weekIndex: number;
  weekCount: number;
}): boolean {
  return weekCount - 1 - weekIndex < 2;
}

function ActivityGraphHeaderRows({
  weeks,
  monthLabels,
}: {
  weeks: ProfileActivityWeek[];
  monthLabels: string[];
}) {
  return (
    <>
      <div aria-hidden />
      {monthLabels.map((monthKey, weekIndex) => (
        <span key={`month-${weekIndex}`} className="relative min-h-2.5 min-w-0">
          {monthKey ? (
            <span
              className={cn(
                "absolute bottom-0 whitespace-nowrap text-[10px] leading-none text-muted-foreground",
                monthLabelAlignsEnd({ weekIndex, weekCount: weeks.length }) ? "right-0" : "left-0",
              )}
            >
              {formatMonthLabel(monthKey)}
            </span>
          ) : null}
        </span>
      ))}
      <div aria-hidden />
      {weeks.map((_, weekIndex) => (
        <div key={`month-gap-${weekIndex}`} aria-hidden />
      ))}
      <div aria-hidden />
      {weeks.map((week, weekIndex) => (
        <ChallengeWeekStarCell
          key={`challenge-star-${weekIndex}`}
          challengeCompleted={week.challengeCompleted}
        />
      ))}
    </>
  );
}

function ActivityGraphGrid({
  todayStr,
  weeks,
}: {
  todayStr: string;
  weeks: ProfileActivityWeek[];
}) {
  const monthLabels = monthLabelsForWeeks({ weeks, endDateStr: todayStr });

  return (
    <InfotipProvider delay={300}>
      <div
        className="grid w-full gap-[3px]"
        style={{
          gridTemplateColumns: activityGridColumns(weeks.length),
          gridTemplateRows: "auto 0.5rem repeat(8, minmax(0.625rem, 1fr))",
          minWidth: activityGridMinWidth(weeks.length),
        }}
      >
        <ActivityGraphHeaderRows weeks={weeks} monthLabels={monthLabels} />
        {WEEKDAY_LABELS.map((label, dayIndex) => (
          <Fragment key={label}>
            <span
              className={cn(
                "flex min-h-2.5 min-w-0 items-center overflow-hidden text-[9px] leading-none text-muted-foreground",
                (dayIndex % 2 === 1 || dayIndex === 6) && "opacity-0",
              )}
            >
              {label}
            </span>
            {weeks.map((week, weekIndex) => (
              <ActivityDayCell key={`${weekIndex}-${dayIndex}`} day={week.days[dayIndex]} />
            ))}
          </Fragment>
        ))}
      </div>
    </InfotipProvider>
  );
}

function ActivityGraphBody({
  todayStr,
  weeks,
}: {
  todayStr: string;
  weeks: ProfileActivityWeek[];
}) {
  return (
    <div className="flex w-full min-w-0 flex-col gap-2">
      <div className="w-full min-w-0 overflow-x-auto">
        <ActivityGraphGrid todayStr={todayStr} weeks={weeks} />
      </div>
      <ActivityFillLegend className="self-end" />
    </div>
  );
}

function ProfileActivityGraphSkeleton() {
  return (
    <div className="space-y-2">
      <Skeleton className="h-3 w-48" />
      <Skeleton className="h-[108px] w-full" />
    </div>
  );
}

function ProfileActivityCardHeader() {
  return (
    <CardHeader>
      <CardTitle>Activity</CardTitle>
      <CardDescription>Games you completed over the past year.</CardDescription>
    </CardHeader>
  );
}

export function ProfileActivityCardSkeleton() {
  return (
    <Card>
      <ProfileActivityCardHeader />
      <CardContent>
        <ProfileActivityGraphSkeleton />
      </CardContent>
    </Card>
  );
}

export function ProfileActivityCard({ activity }: { activity: ProfileActivityData }) {
  return (
    <Card>
      <ProfileActivityCardHeader />
      <CardContent>
        <ActivityGraphBody todayStr={activity.todayStr} weeks={activity.weeks} />
      </CardContent>
    </Card>
  );
}
